import React, {useState, useEffect} from "react"
import PropTypes from "prop-types"
import {Row, Col, Modal, ModalHeader} from "react-bootstrap"
import {RouteComponentProps} from "react-router"

import DealByUrlSlug from "@irati/deals/apollo/query/DealByUrlSlug"
import {Card} from "@irati/ui/src/Layout"
import Spacing from "@irati/ui/src/Spacing"
import authentication from "@irati/auth"
import hasPerksGOLocations from "@irati/deals/helpers/deals/hasPerksGOLocations"
import idHelper from "@irati/utils/id"
import redemptionFlow from "@irati/checkout/utils/redemption"
import {Deal} from "@irati/types"
import detailsToWebAddress from "@irati/checkout/utils/redemption/helpers/detailsToWebAddress"
import history from "@irati/utils/history"
import basket from "@irati/checkout/utils/basket"
import tracking from "@irati-tracking/perks/perks"
import withPerkOrReward, {WithPerkOrReward} from "@irati-web/flexi-rewards/src/utils/withPerkOrReward"

import RequireDealStatusOn from "../../components/RequireDealStatusOn"
import DealBanner from "../../components/DealBanner"
import DealHeader from "../../components/Deal/Header"
import DealDescription from "../../components/Deal/Description"
import BirthdayBox from "../../components/Deal/BirthdayBox"
import DealFAQ from "../../components/Deal/FAQ"
import DealDeliveryInfo from "../../components/Deal/DeliveryInfo"
import DealLocations from "../../components/Deal/DealLocations"
import ContactInfo from "../../components/Deal/ContactInfo"
import DealImportantInfo from "../../components/DealImportantInfo"
import MobileContinueButton from "../../components/Deal/Mobile/Continue"
import MobileFlushCol from "../../components/ui/MobileFlushCol"
import DealHowItWorks from "../../components/Deal/HowItWorks"

export type OnDealSubmit = () => Promise<any>

interface Props extends RouteComponentProps<{urlSlug: string}>, WithPerkOrReward {
  id?: string
}

const DealInfo: React.FC<Props> = (props, context) => {
  const [showImportantInfo, setShowImportantInfo] = useState(false)
  const {isRewards} = props

  useEffect(() => {
    basket.clear()
  }, [])

  const urlSlug = props.match.params.urlSlug
  const idNamespace = idHelper("deal-info-block", props)

  const handleSubmit: (deal: Deal) => OnDealSubmit = (deal) => async() => {
    const details = await redemptionFlow.dealInfo(deal)
    tracking.startRedeemPerk(deal)
    const url = detailsToWebAddress(details)
    history.push(url, context)
  }

  return (
    <DealByUrlSlug variables={{urlSlug, isRewards}} Error={RequireDealStatusOn}>
      {({data}) => {
        if (!data || !data.dealBySlug) {
          return <RequireDealStatusOn deal={null} />
        }
        const deal = data.dealBySlug

        tracking.viewPerk(deal)
        const showDealLocations =
          authentication.apps("platform.geoLocation") && authentication.apps("perks.perksGo") && hasPerksGOLocations(deal)

        return (
          <>
            <DealBanner deal={deal} />

            <Row>
              <Spacing marginTop="75px">
                <MobileFlushCol xs={12} md={8}>
                  <Card>
                    <DealHeader id={idNamespace} deal={deal} />
                    <DealDescription id={idNamespace} deal={deal} />
                    <DealHowItWorks id={idNamespace} deal={deal} />
                    {!!deal.isBirthdayBox && <BirthdayBox id={idNamespace} metadata={deal.birthdayBoxMetaData} />}
                    <DealDeliveryInfo id={idNamespace} deal={deal} />
                    {showDealLocations && <DealLocations id={idNamespace} deal={deal} />}
                    <DealFAQ id={idNamespace} deal={deal} />
                    <ContactInfo id={idNamespace} deal={deal} />
                  </Card>
                </MobileFlushCol>
                <Col xsHidden smHidden md={4}>
                  <Card>
                    <DealImportantInfo id={idNamespace} deal={deal} onSubmit={handleSubmit(deal)} allowEarnPointsModal />
                  </Card>
                </Col>
                <Col lgHidden mdHidden sm={12}>
                  <MobileContinueButton onClick={() => setShowImportantInfo(true)} />
                </Col>
              </Spacing>
            </Row>

            {/* On mobile, "important info" is displayed in this modal as an extra step */}
            <Modal show={showImportantInfo} onHide={() => setShowImportantInfo(false)} className="modal-slide-up modal-full-screen">
              <ModalHeader closeButton />
              <Spacing margin={3}>
                <DealImportantInfo id={idNamespace} deal={deal} onSubmit={handleSubmit(deal)} />
              </Spacing>
            </Modal>
          </>
        )
      }}
    </DealByUrlSlug>
  )
}

DealInfo.contextTypes = {
  appPath: PropTypes.string
}

export default withPerkOrReward(DealInfo)
